import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import {
    ArrowRight,
    ArrowSquareOut,
    Ticket,
    Tag,
    Trophy,
    TennisBall,
    MapPin,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { frDate, frShort } from "@/lib/format";

const HERO_IMG =
    "https://images.unsplash.com/photo-1696661629651-c418070919f6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

const fade = (i = 0) => ({
    initial: { opacity: 0, y: 18 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-40px" },
    transition: { duration: 0.4, delay: i * 0.06, ease: "easeOut" },
});

function useCountdown(target) {
    const [left, setLeft] = useState(() => Math.max(0, new Date(target) - new Date()));
    useEffect(() => {
        const id = setInterval(() => setLeft(Math.max(0, new Date(target) - new Date())), 1000);
        return () => clearInterval(id);
    }, [target]);
    const s = Math.floor(left / 1000);
    return {
        done: left <= 0,
        days: Math.floor(s / 86400),
        hours: Math.floor((s % 86400) / 3600),
        mins: Math.floor((s % 3600) / 60),
        secs: s % 60,
    };
}

function Countdown({ target }) {
    const c = useCountdown(target);
    if (c.done) {
        return <div className="mt-4 font-mono text-clay text-sm uppercase tracking-widest" data-testid="countdown-live">C'est aujourd'hui !</div>;
    }
    const units = [
        { v: c.days, l: "j" },
        { v: c.hours, l: "h" },
        { v: c.mins, l: "min" },
        { v: c.secs, l: "s" },
    ];
    return (
        <div className="mt-4 flex gap-2" data-testid="countdown">
            {units.map((u) => (
                <div key={u.l} className="flex flex-col items-center rounded-lg bg-white/10 border border-white/15 px-3 py-2 min-w-[52px]">
                    <span className="font-display font-black text-xl leading-none tabular-nums">{String(u.v).padStart(2, "0")}</span>
                    <span className="font-mono text-[9px] uppercase tracking-widest text-slate-300 mt-1">{u.l}</span>
                </div>
            ))}
        </div>
    );
}

export default function Home() {
    const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
    const { data: news = [] } = useQuery({ queryKey: ["news"], queryFn: async () => (await api.get("/news")).data });
    const { data: champs = [] } = useQuery({ queryKey: ["championships"], queryFn: async () => (await api.get("/championships")).data });

    const latest = news.slice(0, 3);
    const nextChamp = champs.find((c) => new Date(c.date) >= new Date()) || champs[0];

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative overflow-hidden bg-navy-deep text-white">
                <div className="absolute inset-0">
                    <img src={HERO_IMG} alt="Joueur sur court en terre battue" className="h-full w-full object-cover" />
                    <div className="absolute inset-0 bg-gradient-to-r from-navy-deep via-navy-deep/85 to-navy-deep/40" />
                    <div className="absolute inset-0 court-lines opacity-40" />
                </div>
                <div className="relative max-w-6xl mx-auto px-4 md:px-6 py-16 md:py-28">
                    <motion.div {...fade(0)} className="max-w-2xl">
                        <div className="inline-flex items-center gap-2 rounded-full bg-clay/20 border border-clay/40 px-4 py-1.5 mb-6">
                            <TennisBall weight="fill" size={16} className="text-clay" />
                            <span className="overline text-clay">{settings?.season_title || "Nouvelle saison"}</span>
                        </div>
                        <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
                            Tennis Club<br />de La Turballe
                        </h1>
                        <p className="mt-6 text-base md:text-lg text-slate-200 leading-relaxed max-w-xl">
                            {settings?.intro || "Tennis et pickleball au bord de l'océan, du loisir à la compétition, dans une ambiance conviviale."}
                        </p>
                        <div className="mt-8 flex flex-wrap gap-3">
                            <Link
                                to="/inscriptions"
                                data-testid="hero-inscriptions-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3.5 font-bold text-white transition-transform duration-200 hover:-translate-y-0.5 hover:bg-clay-dark"
                            >
                                S'inscrire au club <ArrowRight weight="bold" size={18} />
                            </Link>
                            <a
                                href={settings?.tenup_url || "https://tenup.fft.fr"}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid="hero-tenup-btn"
                                className="inline-flex items-center gap-2 rounded-full bg-white/10 border border-white/25 px-6 py-3.5 font-bold text-white backdrop-blur-sm transition-colors duration-200 hover:bg-white/20"
                            >
                                Réserver sur Ten'Up <ArrowSquareOut weight="bold" size={16} />
                            </a>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* LATEST NEWS */}
            <section className="max-w-6xl mx-auto px-4 md:px-6 pt-12 md:pt-16">
                <div className="flex items-end justify-between mb-8">
                    <div>
                        <div className="overline text-clay mb-2">Le club bouge</div>
                        <h2 className="font-display font-extrabold text-3xl md:text-4xl text-navy tracking-tight">Dernières actualités</h2>
                    </div>
                    <Link to="/actualites" data-testid="all-news-link" className="hidden sm:inline-flex items-center gap-2 font-bold text-navy hover:text-clay transition-colors">
                        Toutes les actus <ArrowRight weight="bold" size={18} />
                    </Link>
                </div>
                <div className="grid gap-5 md:grid-cols-3">
                    {latest.map((n, i) => (
                        <motion.article key={n.id} {...fade(i)} data-testid="home-news-card">
                            <Link to={`/actualites/${n.id}`} className="group block brand-card overflow-hidden h-full transition-colors duration-200 hover:border-clay">
                                <div className="aspect-[16/10] bg-slate-100 overflow-hidden">
                                    <img
                                        src={n.image_url || HERO_IMG}
                                        alt={n.title}
                                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                    />
                                </div>
                                <div className="p-5">
                                    <div className="flex items-center gap-3 text-xs">
                                        <span className="font-mono uppercase tracking-widest text-clay">{n.category}</span>
                                        <span className="text-slate-400">{frShort(n.date)}</span>
                                    </div>
                                    <h3 className="mt-2 font-display font-bold text-lg text-navy leading-snug">{n.title}</h3>
                                    <p className="mt-2 text-sm text-slate-500 line-clamp-2">{n.excerpt}</p>
                                </div>
                            </Link>
                        </motion.article>
                    ))}
                    {latest.length === 0 && (
                        <p className="text-slate-500 col-span-3">Aucune actualité pour le moment.</p>
                    )}
                </div>
            </section>

            {/* BENTO GRID */}
            <section className="max-w-6xl mx-auto px-4 md:px-6 mt-16 md:mt-24 relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 md:gap-5">
                    {/* Season info large */}
                    <motion.div {...fade(0)} className="md:col-span-7 brand-card p-6 md:p-8">
                        <div className="overline text-clay mb-3">Info saison</div>
                        <h2 className="font-display font-extrabold text-2xl md:text-3xl text-navy tracking-tight">
                            {settings?.season_title || "Saison en cours"}
                        </h2>
                        <p className="mt-4 text-slate-600 leading-relaxed">
                            {settings?.season_info || "Les inscriptions sont ouvertes. Retrouvez toutes les informations et documents dans l'espace inscriptions."}
                        </p>
                        <Link to="/inscriptions" data-testid="season-inscriptions-link" className="mt-6 inline-flex items-center gap-2 font-bold text-navy hover:text-clay transition-colors">
                            Voir les inscriptions <ArrowRight weight="bold" size={18} />
                        </Link>
                    </motion.div>

                    {/* Next championship */}
                    <motion.div {...fade(1)} className="md:col-span-5 brand-card p-6 md:p-8 bg-navy text-white relative overflow-hidden">
                        <div className="absolute inset-0 court-lines opacity-30" />
                        <div className="relative">
                            <div className="overline text-clay mb-3">Prochaine rencontre</div>
                            {nextChamp ? (
                                <>
                                    <Trophy weight="duotone" size={32} className="text-clay mb-3" />
                                    <div className="font-display font-extrabold text-xl leading-tight">{nextChamp.team_name}</div>
                                    <div className="mt-2 text-slate-300 text-sm">
                                        vs {nextChamp.opponent || "à définir"} · {nextChamp.home_away}
                                    </div>
                                    <div className="mt-4 font-mono text-clay text-sm uppercase tracking-widest">
                                        {frDate(nextChamp.date, { weekday: "long" })}
                                    </div>
                                    <Countdown target={nextChamp.date} />
                                    <Link to="/competitions" data-testid="hero-champ-link" className="mt-5 inline-flex items-center gap-2 text-sm font-bold hover:text-clay transition-colors">
                                        Tout le calendrier <ArrowRight weight="bold" size={16} />
                                    </Link>
                                </>
                            ) : (
                                <p className="text-slate-300 text-sm">Le calendrier des compétitions sera bientôt disponible.</p>
                            )}
                        </div>
                    </motion.div>

                    {/* Quick access trio */}
                    {[
                        { to: "/inscriptions", icon: Ticket, label: "Inscriptions", desc: "Dossiers & documents à télécharger", id: "inscriptions" },
                        { to: "/offres", icon: Tag, label: "Offre du club", desc: "Adhésions, cours & tarifs 2026-2027", id: "offres" },
                        { to: "/competitions", icon: Trophy, label: "Compétitions", desc: "Championnats par équipes", id: "competitions" },
                    ].map((q, i) => (
                        <motion.div key={q.to} {...fade(i)} className="md:col-span-4">
                            <Link
                                to={q.to}
                                data-testid={`quick-${q.id}-card`}
                                className="group block brand-card p-6 md:p-8 h-full transition-colors duration-200 hover:border-clay"
                            >
                                <q.icon weight="duotone" size={34} className="text-clay" />
                                <div className="mt-4 font-display font-extrabold text-xl text-navy">{q.label}</div>
                                <p className="mt-1.5 text-sm text-slate-500">{q.desc}</p>
                                <div className="mt-4 inline-flex items-center gap-2 text-sm font-bold text-navy group-hover:gap-3 transition-all duration-200">
                                    Découvrir <ArrowRight weight="bold" size={16} />
                                </div>
                            </Link>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* CONTACT STRIP */}
            <section className="max-w-6xl mx-auto px-4 md:px-6 mt-16 md:mt-24">
                <div className="brand-card bg-navy-deep text-white p-8 md:p-12 relative overflow-hidden">
                    <div className="absolute inset-0 court-lines opacity-30" />
                    <div className="relative flex flex-col md:flex-row md:items-center justify-between gap-6">
                        <div>
                            <div className="overline text-clay mb-3">Nous trouver</div>
                            <h2 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight">Venez taper la balle !</h2>
                            <p className="mt-3 text-slate-300 flex items-center gap-2">
                                <MapPin weight="fill" size={18} className="text-clay" /> {settings?.address || "La Turballe"}
                            </p>
                        </div>
                        <Link to="/contact" data-testid="home-contact-btn" className="inline-flex items-center gap-2 rounded-full bg-clay px-6 py-3.5 font-bold self-start md:self-auto transition-transform duration-200 hover:-translate-y-0.5 hover:bg-clay-dark">
                            Nous contacter <ArrowRight weight="bold" size={18} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
